import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from '@tanstack/react-router';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { ChatHeader } from '../../components/Pleri/components/ChatBox/ChatHeader';
import { Sidebar } from '../../components/Pleri/components/ChatBox/Sidebar';
import type { OnboardingStep } from '../../components/Pleri/components/ChatBox/OnboardingProgressSteps';
import {
  getOnboardingSteps as fetchOnboardingSteps,
  dismissOnboardingStep,
} from '../../api/onboarding';
import { useSidebarStore } from '../../store/sidebarStore';
import { useLayoutStore } from '../../store/layoutStore';
import { usePleriRenderOptional } from '../../components/Pleri/context/usePleriRenderOptional';
import { PleriClient } from '../../components/Pleri/client/PleriClient';
import { pleriRouter } from '../../Pleri';

interface FullScreenLayoutProps {
  minimize: boolean;
}

const backdropVariants: Variants = {
  hidden: {
    opacity: 0,
    backdropFilter: 'blur(0px)',
  },
  visible: {
    opacity: 1,
    backdropFilter: 'blur(8px)',
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1] as const,
    },
  },
  exit: {
    opacity: 0,
    backdropFilter: 'blur(0px)',
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1] as const,
    },
  },
};

const containerVariants: Variants = {
  hidden: {
    scale: 0.85,
    opacity: 0,
    y: 20,
  },
  visible: {
    scale: 1,
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring' as const,
      stiffness: 300,
      damping: 30,
      mass: 0.8,
      delay: 0.1,
    },
  },
  exit: {
    scale: 0.9,
    opacity: 0,
    y: 10,
    transition: {
      duration: 0.25,
      ease: [0.4, 0, 0.2, 1] as const,
    },
  },
};

const sidebarVariants: Variants = {
  hidden: {
    x: -50,
    opacity: 0,
  },
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      type: 'spring' as const,
      stiffness: 300,
      damping: 30,
      delay: 0.2,
    },
  },
  exit: {
    x: -30,
    opacity: 0,
    transition: {
      duration: 0.2,
      ease: [0.4, 0, 0.2, 1] as const,
    },
  },
};

const contentVariants: Variants = {
  hidden: {
    x: 30,
    opacity: 0,
  },
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      type: 'spring' as const,
      stiffness: 300,
      damping: 30,
      delay: 0.25,
    },
  },
  exit: {
    x: 20,
    opacity: 0,
    transition: {
      duration: 0.2,
      ease: [0.4, 0, 0.2, 1] as const,
    },
  },
};

export const FullScreenLayout = ({ minimize }: FullScreenLayoutProps) => {
  const { isOpen: isSidebarOpen, toggleSidebar } = useSidebarStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { setMiniMode, minimizeToMiniMode } = useLayoutStore();
  const pleriRenderContext = usePleriRenderOptional();
  const [onboardingSteps, setOnboardingSteps] = useState<OnboardingStep[]>([]);

  useEffect(() => {
    setMiniMode(false);
  }, [setMiniMode]);

  // Normalize pathname (remove /pleri prefix if present)
  const pathname = location.pathname.replace('/pleri', '');
  const isConversationRoute =
    pathname.startsWith('/c/') || pathname === '/c' || pathname === '/c/';

  useEffect(() => {
    // Fetch onboarding steps on mount
    fetchOnboardingSteps().then(setOnboardingSteps).catch(console.error);
  }, []);

  // Refetch onboarding steps when pathname changes and we're on a conversation route
  useEffect(() => {
    if (isConversationRoute) {
      fetchOnboardingSteps().then(setOnboardingSteps).catch(console.error);
    }
  }, [pathname, isConversationRoute]);

  const handleClose = () => {
    minimizeToMiniMode();
    if (pleriRenderContext) {
      pleriRenderContext.close();
    }
    navigate({ to: '/trigger' });
  };

  const handleOnboardingStepAction = (step: OnboardingStep) => {
    if (!step.action) {
      return;
    }

    // Handle prompt action - send to chat
    if (step.action.prompt) {
      PleriClient.updateChatInput({
        prompt: step.action.prompt,
        autoSubmit: false,
        useExistingConversation: false,
      });
    }

    // Handle URL action - navigate within search MFE or open in new page
    else if (step.action.url) {
      try {
        // If newWindow is true, open URL in a new tab
        if (step.action.newWindow) {
          // If it's a relative path, construct full URL
          if (step.action.url.startsWith('/')) {
            const fullUrl = new URL(step.action.url, window.location.origin);
            window.open(fullUrl.toString(), '_blank', 'noopener,noreferrer');
          } else {
            // Absolute URL - open directly
            window.open(step.action.url, '_blank', 'noopener,noreferrer');
          }
          return;
        }

        // If it's a relative path (starts with /), use router navigation
        if (step.action.url.startsWith('/')) {
          const url = new URL(step.action.url, window.location.origin);
          const pathname = url.pathname;
          const searchParams = new URLSearchParams(url.search);

          // Build search object
          const searchObj: Record<string, string> = {};
          searchParams.forEach((value, key) => {
            searchObj[key] = value;
          });

          // Use pleriRouter for navigation - handles all routes dynamically
          pleriRouter.navigate({
            to: pathname,
            search: Object.keys(searchObj).length > 0 ? searchObj : undefined,
          });
          return;
        } else {
          // Absolute URL or external link - use window.location
          window.location.href = step.action.url;
        }
      } catch (error) {
        console.error('Failed to parse onboarding step URL:', error);
        // Fallback: use window.location for the URL
        window.location.href = step.action.url;
      }
    }
  };

  const handleOnboardingStepDismiss = async (step: OnboardingStep) => {
    try {
      await dismissOnboardingStep(step.id);
      // Refetch onboarding steps after dismissal
      fetchOnboardingSteps().then(setOnboardingSteps).catch(console.error);
    } catch (error) {
      console.error('Failed to dismiss onboarding step:', error);
    }
  };

  return (
    <AnimatePresence mode="wait">
      {!minimize && (
        <motion.div
          key="fullscreen-layout"
          className="fixed inset-0 z-[2147482642]"
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {/* Backdrop */}
          <motion.div
            variants={backdropVariants}
            className="absolute inset-0 bg-gray-900/10"
            style={{ backdropFilter: 'blur(8px)' }}
          />
          {/* Chat Container */}
          <motion.div
            variants={containerVariants}
            className="absolute inset-4 flex overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-gray-900"
          >
            {/* Sidebar - Full height in its own column */}
            <motion.div variants={sidebarVariants}>
              <Sidebar
                isCollapsed={!isSidebarOpen}
                onToggleCollapsed={toggleSidebar}
              />
            </motion.div>

            {/* Main Content Area - Header and body in same column */}
            <motion.div
              variants={contentVariants}
              className="flex flex-1 flex-col overflow-hidden"
            >
              {/* Header - shows breadcrumb for non-conversation routes, conversation title for conversation routes */}
              <ChatHeader
                isFullScreen={true}
                onFullScreenToggle={() => {
                  minimizeToMiniMode();
                  navigate({
                    to: '/c/$conversationId',
                    params: { conversationId: 'active' },
                  });
                }}
                onClose={handleClose}
                className="flex-shrink-0 px-4 py-2"
                onboardingSteps={onboardingSteps}
                onOnboardingStepAction={handleOnboardingStepAction}
                onOnboardingStepDismiss={handleOnboardingStepDismiss}
              />

              {/* Body - Scrollable */}
              <div className="relative flex flex-1 flex-row overflow-y-auto">
                <Outlet />
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
